/**
 * Talent announcements (prototype v108 §5.1), Admin and Talent: broadcast to all talent in Good Standing, or only
 * those within a radius of a ZIP. Preview shows how many will receive it before anything is sent. Rules:
 * src/lib/staging/announcements.js.
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Textarea } from '@/components/admin/ui/textarea';
import { RADIUS } from '@/lib/staging/announcements';
import { stagingFetch } from '../api';

export default function AnnouncementsCard() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [zip, setZip] = useState('');
  const [radius, setRadius] = useState(RADIUS.default);
  const [busy, setBusy] = useState(false);
  const body = () => JSON.stringify({ message, zip: zip.trim() || undefined, radius });

  const post = async () => {
    setBusy(true);
    try {
      const preview = await stagingFetch('/api/admin/staging/talent/announcements', { method: 'POST', body: JSON.stringify({ ...JSON.parse(body()), dry_run: true }) });
      if (!preview.count) { toast.error(`No talent in Good Standing ${zip.trim() ? preview.where : ''}`.trim()); return; }
      if (!window.confirm(`Send this announcement to ${preview.count} talent (${preview.where})? Each gets it as a message in their portal.`)) return;
      const r = await stagingFetch('/api/admin/staging/talent/announcements', { method: 'POST', body: body() });
      toast.success(`Announcement sent to ${r.sent} talent`);
      setMessage(''); setZip(''); setRadius(RADIUS.default); setOpen(false);
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Announcements</h3>
          <p className="text-xs text-muted-foreground">Broadcast a message to all talent or limit by ZIP/radius. Bulk direct messaging is handled from the Talent table below.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>{open ? 'Close' : 'Create Announcement'}</Button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <Textarea aria-label="Announcement message" rows={3} placeholder="Type announcement message..." value={message} onChange={(e) => setMessage(e.target.value)} />
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-sm">Target ZIP (optional)<Input aria-label="Target ZIP" className="mt-1 w-32" inputMode="numeric" maxLength={5} placeholder="ZIP code" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))} /></label>
            <label className="text-sm">Radius <span className="tabular-nums font-medium">{radius} mi</span>
              <input aria-label="Radius" type="range" className="mt-1 block w-56" min={RADIUS.min} max={RADIUS.max} step={RADIUS.step} value={radius} disabled={!zip} onChange={(e) => setRadius(Number(e.target.value))} />
            </label>
            <p className="text-[11px] text-muted-foreground max-w-xs">Goes to talent in Good Standing{zip ? `, within ${radius} miles of ${zip}` : ''}. Incomplete and Blacklisted talent are left out.</p>
            <Button className="ml-auto" disabled={busy || !message.trim() || (zip.length > 0 && zip.length !== 5)} onClick={post}>Post Announcement</Button>
          </div>
        </div>
      )}
    </section>
  );
}
